/**
 * Settings Store
 *
 * User preferences and application settings
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Settings {
  theme: 'light' | 'dark';
  language: string;
  notifications: boolean;
  timezone: string;
  customSettings?: Record<string, unknown>;
}

export interface SettingsStore {
  settings: Settings;
  updateSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  updateSettings: (updates: Partial<Settings>) => void;
  getSetting: <K extends keyof Settings>(key: K) => Settings[K];
  resetSettings: () => void;
}

const defaultSettings: Settings = {
  theme: 'dark',
  language: 'en-US',
  notifications: true,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => ({
      settings: defaultSettings,

      updateSetting: <K extends keyof Settings>(key: K, value: Settings[K]) =>
        set((state) => ({
          settings: { ...state.settings, [key]: value },
        })),

      updateSettings: (updates: Partial<Settings>) =>
        set((state) => ({
          settings: { ...state.settings, ...updates },
        })),

      getSetting: <K extends keyof Settings>(key: K) => {
        const state = get();
        return state.settings[key];
      },

      resetSettings: () => set({ settings: defaultSettings }),
    }),
    { name: 'edge-interact-settings' }
  )
);
